import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ErrorNotification,
  Pagination,
  ActivityRunResponse,
} from "@via/utils/interfaces.tsx";
import type {
  ListRunSchedulerSourcesResponseBody,
  RunScheduler,
  RunSchedulerExecution,
  RunSchedulerHistoryResponseBody,
  RunSchedulerSourceState,
  RunSchedulerSummaryResponseBody,
} from "common";

export const SCHEDULERS_API_BASE = "/api/run/v1/schedulers";
export const RUN_LIST_API_BASE = "/api/run/v1/list";
export const SCHEDULERS_SUMMARY_API = "/api/run/v1/schedulers/summary";

export type RunSchedulerListResponse =
  | Pagination<RunScheduler>
  | RunScheduler[];

export const fetchAccessibleRuns = async (
  take: number = 100,
  skip: number = 0
): Promise<Pagination<ActivityRunResponse>> => {
  const queryParams = new URLSearchParams({
    take: `${take}`,
    skip: `${skip}`,
    sort: "dateCreatedLastRun",
    order: "desc",
  });
  const url = `${RUN_LIST_API_BASE}?${queryParams.toString()}`;
  const res = await axios.post(url, { searchKey: "" });
  return res.data;
};

export const useAccessibleRuns = (
  take: number = 100,
  skip: number = 0,
  enabled: boolean = true
) => {
  return useQuery<Pagination<ActivityRunResponse>, ErrorNotification>({
    queryKey: [RUN_LIST_API_BASE, take, skip],
    queryFn: async () => await fetchAccessibleRuns(take, skip),
    retry: false,
    enabled,
  });
};

export const useSchedulerList = () => {
  return useQuery<RunSchedulerListResponse, ErrorNotification>({
    queryKey: [SCHEDULERS_API_BASE],
    queryFn: async () => {
      const res = await axios.get(SCHEDULERS_API_BASE);
      return res.data;
    },
    retry: false,
  });
};

export const useSchedulerSummary = () => {
  return useQuery<RunSchedulerSummaryResponseBody, ErrorNotification>({
    queryKey: [SCHEDULERS_SUMMARY_API],
    queryFn: async () => {
      const res = await axios.get(SCHEDULERS_SUMMARY_API);
      return res.data;
    },
    retry: false,
    // Keep counts fresh while the schedulers page is open.
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });
};

export const useSchedulerSourcesByState = (
  schedulerId: string | number | null,
  state: RunSchedulerSourceState,
  take: number = 50,
  skip: number = 0,
  enabled: boolean = true
) => {
  return useQuery<ListRunSchedulerSourcesResponseBody, ErrorNotification>({
    queryKey: [
      `${SCHEDULERS_API_BASE}/${schedulerId}/sources`,
      state,
      take,
      skip,
    ],
    queryFn: async () => {
      const res = await axios.get(
        `${SCHEDULERS_API_BASE}/${schedulerId}/sources`,
        {
          params: { state, take, skip },
        }
      );
      return res.data;
    },
    retry: false,
    enabled: enabled && !!schedulerId,
  });
};

export const useCreateScheduler = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    RunScheduler,
    ErrorNotification,
    Omit<RunScheduler, "id" | "createdAt" | "updatedAt">
  >({
    mutationFn: async payload => {
      const res = await axios.post(SCHEDULERS_API_BASE, payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "scheduler-create-success",
        message: "Scheduler created",
        details: "",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({ queryKey: [SCHEDULERS_API_BASE] });
    },
    onError: error => {
      setNotification({
        id: "scheduler-create-failed",
        message: "Scheduler create failed",
        details:
          error?.response?.data?.message ??
          "An error occurred, please try again.",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateScheduler = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    RunScheduler,
    ErrorNotification,
    { id: string | number; data: Partial<RunScheduler> & { config?: unknown } }
  >({
    mutationFn: async ({ id, data }) => {
      const res = await axios.patch(`${SCHEDULERS_API_BASE}/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULERS_API_BASE] });
    },
    onError: error => {
      setNotification({
        id: "scheduler-update-failed",
        message: "Scheduler update failed",
        details:
          error?.response?.data?.message ??
          "An error occurred, please try again.",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteScheduler = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<void, ErrorNotification, { id: string | number }>({
    mutationFn: async ({ id }) => {
      await axios.delete(`${SCHEDULERS_API_BASE}/${id}`);
    },
    onSuccess: () => {
      setNotification({
        id: "scheduler-delete-success",
        message: "Scheduler deleted",
        details: "",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({ queryKey: [SCHEDULERS_API_BASE] });
    },
    onError: error => {
      setNotification({
        id: "scheduler-delete-failed",
        message: "Scheduler delete failed",
        details:
          error?.response?.data?.message ??
          "An error occurred, please try again.",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useTriggerScheduler = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    { executionId: number; status: string },
    ErrorNotification,
    { id: string | number }
  >({
    mutationFn: async ({ id }) => {
      try {
        const res = await axios.post(`${SCHEDULERS_API_BASE}/${id}/trigger`);
        return res.data;
      } catch (e: any) {
        // 409 means scheduler is already running; treat as a non-fatal response
        const status = e?.response?.status;
        if (status === 409 && e?.response?.data?.executionId) {
          return e.response.data as { executionId: number; status: string };
        }
        throw e;
      }
    },
    onSuccess: (data, vars) => {
      setNotification({
        id: "scheduler-triggered",
        message:
          data.status === "running"
            ? "Scheduler already running"
            : "Scheduler triggered",
        details: "",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [`${SCHEDULERS_API_BASE}/${vars.id}/history`],
      });
    },
    onError: error => {
      setNotification({
        id: "scheduler-trigger-failed",
        message: "Scheduler trigger failed",
        details:
          error?.response?.data?.message ??
          "An error occurred, please try again.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useSchedulerHistory = (
  schedulerId: string | number | null,
  take: number = 20,
  skip: number = 0,
  enabled: boolean = true
) => {
  return useQuery<RunSchedulerHistoryResponseBody, ErrorNotification>({
    queryKey: [`${SCHEDULERS_API_BASE}/${schedulerId}/history`, take, skip],
    queryFn: async () => {
      const res = await axios.get(
        `${SCHEDULERS_API_BASE}/${schedulerId}/history`,
        {
          params: { take, skip },
        }
      );
      return res.data;
    },
    retry: false,
    enabled: enabled && !!schedulerId,
  });
};

export const useSchedulerExecution = (
  schedulerId: string | number | null,
  executionId: number | null,
  enabled: boolean = true
) => {
  return useQuery<RunSchedulerExecution, ErrorNotification>({
    queryKey: [
      `${SCHEDULERS_API_BASE}/${schedulerId}/executions/${executionId}`,
    ],
    queryFn: async () => {
      const res = await axios.get(
        `${SCHEDULERS_API_BASE}/${schedulerId}/executions/${executionId}`
      );
      return res.data;
    },
    retry: false,
    enabled: enabled && !!schedulerId && !!executionId,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data) return false;
      const isRunning = !data.finishedAt || data.outcome === "running";
      return isRunning ? 2000 : false;
    },
  });
};

export async function fetchRunConfigJson(runId: string): Promise<unknown> {
  const res = await axios.get(`/api/run/v1/${runId}/config/download`, {
    responseType: "blob",
    withCredentials: true,
  });
  const blob = res.data as Blob;
  const text = await blob.text();
  try {
    return JSON.parse(text);
  } catch {
    // Some configs might not be pure JSON; return as string so user can paste/edit.
    return text;
  }
}

export const useFetchRunConfig = () => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<unknown, ErrorNotification, { runId: string }>({
    mutationFn: async ({ runId }) => fetchRunConfigJson(runId),
    onError: error => {
      setNotification({
        id: "run-config-fetch-failed",
        message: "Failed to load run config",
        details:
          error?.response?.data?.message ??
          "An error occurred, please try again.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
